import { IEmployeeAffiliateMethod } from '@/api/types/payout';

/** Синхронно с payout-service MethodSetting::ContractMethods */
export const CONTRACT_PAYOUT_METHOD_IDS = ['contract_v1', 'contract_kz', 'contract_eur'] as const;

export const CONTRACT_MANAGER_REJECT_STATUSES = ['approved', 'new', 'partner_edited'] as const;

export const CONTRACT_MANAGER_APPROVE_STATUSES = [
    'partner_rejected',
    'manager_rejected',
    'new',
    'partner_edited',
] as const;

/** Подсказка при disabled «Одобрить» у договорного способа без legal_id */
export const CONTRACT_APPROVE_REQUISITES_HINT = 'Не заполнены реквизиты для одобрения (нет legal_id)';

export const CONTRACT_BULK_APPROVE_REQUISITES_HINT = 'В одной или нескольких заявках не заполнены реквизиты (нет legal_id)';

export interface ContractApproveManagerData {
    legal_id: string;
    legal_name?: string;
}

export type ContractPayoutMethodId = typeof CONTRACT_PAYOUT_METHOD_IDS[number];

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function isContractPayoutMethod(methodId?: string): methodId is ContractPayoutMethodId {
    return CONTRACT_PAYOUT_METHOD_IDS.includes(methodId as ContractPayoutMethodId);
}

export function canManagerRejectContractMethod(status: string): boolean {
    return (CONTRACT_MANAGER_REJECT_STATUSES as readonly string[]).includes(status);
}

export function canManagerApproveContractMethod(status: string): boolean {
    return (CONTRACT_MANAGER_APPROVE_STATUSES as readonly string[]).includes(status);
}

/** Реквизиты договорного способа для PATCH .../approve (поле `data` в теле). */
export function getContractApproveManagerData(
    method: Pick<IEmployeeAffiliateMethod, 'manager_data' | 'partner_data'>,
): ContractApproveManagerData | null {
    const managerData = isPlainRecord(method.manager_data) ? method.manager_data : null;
    const partnerData = isPlainRecord(method.partner_data) ? method.partner_data : null;

    const legalId = managerData?.legal_id ?? partnerData?.legal_id;
    if (legalId == null || legalId === '') {
        return null;
    }

    const result: ContractApproveManagerData = { legal_id: String(legalId) };

    const legalName = managerData?.legal_name ?? partnerData?.legal_name;
    if (legalName != null && legalName !== '') {
        result.legal_name = String(legalName);
    }

    return result;
}

export function buildApproveAffiliateMethodPayload(
    item: Pick<IEmployeeAffiliateMethod, 'method_id' | 'manager_data' | 'partner_data'>,
): { data: ContractApproveManagerData } | undefined {
    if (!isContractPayoutMethod(item.method_id)) {
        return undefined;
    }

    const managerData = getContractApproveManagerData(item);

    return managerData ? { data: managerData } : undefined;
}

export function isContractApproveDataMissing(
    item: Pick<IEmployeeAffiliateMethod, 'method_id' | 'status' | 'manager_data' | 'partner_data'>,
): boolean {
    if (!isContractPayoutMethod(item.method_id)) {
        return false;
    }

    if (!canManagerApproveContractMethod(item.status ?? '')) {
        return false;
    }

    return getContractApproveManagerData(item) === null;
}

export function shouldHideManagerRejectButton(methodId: string | undefined, status: string): boolean {
    if (isContractPayoutMethod(methodId)) {
        return !canManagerRejectContractMethod(status);
    }

    return ['manager_rejected', 'partner_rejected', 'deleted'].includes(status);
}

export function shouldHideManagerApproveButton(methodId: string | undefined, status: string): boolean {
    if (isContractPayoutMethod(methodId)) {
        return !canManagerApproveContractMethod(status);
    }

    return ['approved', 'deleted'].includes(status);
}
